import { Reveal } from "@/components/bits";
import { FeaturedQuote, Hl } from "@/components/Testimonios";

const TIMELINE = [
  {
    weeks: "Semana 1 — 2",
    author: "Mariana Enríquez",
    books: [
      { title: "Las cosas que perdimos en el fuego", cover: "/images/books/las-cosas-que-perdimos.jpg" },
      { title: "Un lugar soleado para gente sombría", cover: "/images/books/un-lugar-soleado.jpg" },
    ],
    text: (
      <>
        Cuentos de varios de sus libros, en especial de{" "}
        <em className="font-serif text-bone">Las cosas que perdimos en el fuego</em> y{" "}
        <em className="font-serif text-bone">Un lugar soleado para gente sombría</em>. La entrada al círculo: el
        horror con historia y reflexiones sobre la sociedad de hoy.
      </>
    ),
  },
  {
    weeks: "Semana 3 — 4",
    author: "Alejandra Pizarnik",
    books: [{ title: "Poesía completa", cover: "/images/books/poesia-completa.jpg" }],
    text: (
      <>
        Lectura íntima de su <em className="font-serif text-bone">Poesía completa</em> y una selección de sus
        diarios. Taller sobre la imagen poética y el silencio como recurso.
      </>
    ),
  },
  {
    weeks: "Semana 5 — 6",
    author: "Sara Gallardo",
    books: [
      { title: "Enero", cover: "/images/books/enero.jpg" },
      { title: "El país del humo", cover: "/images/books/el-pais-del-humo.jpg" },
    ],
    text: (
      <>
        <em className="font-serif text-bone">Enero</em> y una selección de cuentos de{" "}
        <em className="font-serif text-bone">El país del humo</em>. Lectura crítica de la lengua, el paisaje y la
        voz periférica.
      </>
    ),
  },
  {
    weeks: "Semana 7 — 8",
    author: "Gabriela Cabezón Cámara",
    books: [{ title: "La virgen cabeza", cover: "/images/books/la-virgen-cabeza.jpg" }],
    text: (
      <>
        <em className="font-serif text-bone">La virgen cabeza</em> y textos selectos. La escritura desde los
        márgenes, el deseo como política y la lengua como territorio.
      </>
    ),
  },
];

export default function Cronograma() {
  return (
    <section id="cronograma" data-testid="cronograma-section" className="bg-coal py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <Reveal>
            <p className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" /> Cronograma
            </p>
            <h2 className="mt-8 font-serif text-3xl leading-[1.12] tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Dos meses, <span className="font-semibold not-italic text-gold">cuatro viajes.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="self-end">
            <p className="max-w-md text-sm font-light leading-relaxed text-ash sm:text-base">
              Cada autora tiene su tiempo y su propio material para acompañar cada lectura.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="cronograma-sesiones"
            className="relative mt-12 overflow-hidden border border-crimson-light/50 bg-crimson/10 p-8 text-center sm:p-10"
          >
            <div
              className="pointer-events-none absolute -left-16 -top-16 h-56 w-56 rounded-full bg-crimson/30 blur-3xl"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-gold/15 blur-3xl"
              aria-hidden="true"
            />
            <p className="relative text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
              Las sesiones son en vivo · virtuales por Zoom
            </p>
            <p className="relative mt-4 font-serif text-3xl tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Todos los lunes de 19 a 20 hs{" "}
              <span className="whitespace-nowrap text-2xl text-gold sm:text-3xl lg:text-4xl">(hora Argentina)</span>
            </p>
            <p className="relative mt-5 text-sm font-light text-bone/80 sm:text-base">
              Comienzo: <span className="font-semibold text-gold">7 de septiembre</span> · Finalización:{" "}
              <span className="font-semibold text-gold">26 de octubre</span>
            </p>
          </div>
        </Reveal>

        <div className="mt-16 border-t border-cream/10">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.author} delay={i * 0.05}>
              <div data-testid={`cronograma-item-${i}`} className="border-b border-cream/10 py-10">
                <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">{t.weeks}</p>
                <div className="mt-6 flex items-start gap-6 sm:gap-10">
                  <div className="flex shrink-0 gap-3">
                    {t.books.map((b) => (
                      <img
                        key={b.title}
                        src={b.cover}
                        alt={`Cubierta de ${b.title}`}
                        data-testid={`cronograma-cover-${i}-${b.title.slice(0, 6).replace(/\s/g, "-").toLowerCase()}`}
                        loading="lazy"
                        className={`shrink-0 border border-cream/15 shadow-[8px_8px_0_0_rgba(82,0,3,0.45)] transition-transform duration-500 hover:-translate-y-1 ${
                          t.books.length > 1 ? "w-16 sm:w-24" : "w-20 sm:w-28"
                        }`}
                      />
                    ))}
                  </div>
                  <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2 md:items-baseline md:gap-8">
                    <div>
                      <h3 className="font-serif text-2xl tracking-tight text-cream sm:text-3xl">{t.author}</h3>
                      {t.books.map((b) => (
                        <p key={b.title} className="mt-2 font-serif text-lg italic leading-snug text-gold">
                          {b.title}
                        </p>
                      ))}
                    </div>
                    <p className="max-w-lg text-base font-light leading-relaxed text-ash/90">{t.text}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-16">
          <FeaturedQuote testid="testimonio-tatiana" name="Tatiana" place="Lectora del Club · febrero a la actualidad">
            “Antes del club me costaba sostener lecturas que no fueran las académicas.{" "}
            <Hl>
              Con el club le di un lugar a la lectura por placer en lo cotidiano: leer con calma y detenidamente
              para compartir luego en el espacio grupal
            </Hl>
            . Superó mis expectativas:{" "}
            <Hl>
              lo más lindo fue el intercambio ameno donde todas las voces tuvieron lugar y se fueron entretejiendo
            </Hl>
            . La paciencia del agua sobre cada piedra me queda asociada a una gratísima experiencia de lectura
            colectiva. Valoro la amorosidad y la calidez de Aldana y las guías que acompañan la lectura.{" "}
            <Hl>Era lo que necesitaba</Hl>. Lo recomiendo mucho.”
          </FeaturedQuote>
        </Reveal>
      </div>
    </section>
  );
}
